import { atom, selector } from "recoil"

import { UserRole } from "./pluginStudio/types"
import persistAtom from "./recoilPersist"

/**
 * Enum for plugin approval status (frontend)
 */
export enum PluginApprovalStatus {
  PRIVATE = "private",
  AWAITING_REVIEW = "awaiting-review",
  REVIEW_APPROVED = "review-approved",
  REVIEW_DENIED = "review-denied",
  CERTIFIED = "certified",
  LETS_HANG_OWNED = "lets-hang-owned",
}

// Plugin data interface
export interface Plugin {
  id: string
  manifest: {
    name: string
    description: string
    version: string
    author: string
    searchTags?: string[]
    requiresZeroConfiguration?: boolean
    requiresAuthentication?: boolean
    approvalStatus?: PluginApprovalStatus
    [key: string]: any
  }
  code?: {
    transpiledFrontendGuestCodeWeb?: string
    transpiledFrontendHostViewCodeWeb?: string
    transpiledFrontendHostConfigCodeWeb?: string
    customViews?: {
      [viewName: string]: {
        sourceCode?: string
        transpiledCode?: string
        visibleToRole?: string
        fileName?: string
      }
    }
  }
}

// Plugin instance interface
export interface PluginInstance {
  id?: string
  pluginId: string
  eventId: string
  userId: string
  createdAt: Date
  updatedAt: Date
  configuration: any
  enabled: boolean
}

// Enabled Plugin summary interface (for the event document)
export interface EnabledPlugin {
  id: string
  version: string
}

// Plugin cache entry that includes version for cache invalidation
export interface PluginCacheEntry {
  plugin: Plugin
  version: string
  timestamp: number // When this was cached
}

// Plugin configuration cache entry
export interface PluginConfigCacheEntry {
  configuration: any
  timestamp: number
}

// Plugin details state including loading status
export interface PluginDetailsState {
  plugin: Plugin | null
  instance: PluginInstance | null
  isConnected: boolean
  userRole: UserRole
  isLoading: boolean
  hasError: boolean
  errorMessage?: string
}

// Default plugin details state
const defaultPluginDetailsState: PluginDetailsState = {
  plugin: null,
  instance: null,
  isConnected: false,
  userRole: UserRole.GUEST,
  isLoading: false,
  hasError: false,
  errorMessage: undefined,
}

// Plugin details atom - includes the current plugin being viewed
export const pluginDetailsState = atom<PluginDetailsState>({
  key: "pluginDetailsState",
  default: defaultPluginDetailsState,
})

// Cache state for plugin data to avoid refetching
export const pluginCacheState = atom<Record<string, PluginCacheEntry>>({
  key: "pluginCacheState",
  default: {},
  effects_UNSTABLE: [persistAtom],
})

// Cache state for plugin configurations by event and plugin
export const pluginConfigCacheState = atom<
  Record<string, Record<string, PluginConfigCacheEntry>>
>({
  key: "pluginConfigCacheState",
  default: {},
  effects_UNSTABLE: [persistAtom],
})

// Cache state for plugin instances by event
export const pluginInstanceCacheState = atom<
  Record<string, Record<string, PluginInstance>>
>({
  key: "pluginInstanceCacheState",
  default: {},
  effects_UNSTABLE: [persistAtom],
})

// Selector to get all plugins for a specific event
export const eventPluginsSelector = selector({
  key: "eventPluginsSelector",
  get: ({ get }) => {
    const pluginCache = get(pluginCacheState)
    const instanceCache = get(pluginInstanceCacheState)

    return {
      getEventPlugins: (eventId: string) => {
        const eventInstances = instanceCache[eventId] || {}

        return Object.values(eventInstances).map((instance) => {
          const pluginCacheEntry = pluginCache[instance.pluginId]
          return {
            instance,
            plugin: pluginCacheEntry?.plugin,
          }
        })
      },
    }
  },
})
